import { Heart, Gem, GraduationCap, Baby, Cake, Trophy, Building2, Sparkles, type LucideIcon } from 'lucide-react';
import type { EventType, ModuleKey } from '@/types';

// Single source of truth for everything that varies per event type.
// Adding a new event type means adding a member to the EventType union and
// filling in one entry here - the Record forces exhaustiveness.

export interface EventTypeConfig {
  type: EventType;
  label: string;
  /** Onboarding type-card description */
  description: string;
  icon: LucideIcon;
  /** Tailwind classes for the onboarding type card */
  color: string;
  bg: string;
  modules: ModuleKey[];
  /** Onboarding name field; couple types (bryllup/forlovelse) use partner fields instead */
  nameField?: { label: string; placeholder: string };
  /** Recommended vendor categories on the Leverandører page */
  vendorCategories: string[];
  /** Seeded on first visit to Gjøremål */
  defaultTasks: { title: string; category: string }[];
  /** Seeded on first visit to Budsjett */
  defaultBudgetItems: { name: string; category: string; estimated: number }[];
  /** Default guest list groups */
  defaultGuestGroups: string[];
  /** Suffix for the main day in Program, e.g. "Lørdag - bryllupsdagen" */
  scheduleDayLabel?: string;
}

export const EVENT_TYPE_CONFIG: Record<EventType, EventTypeConfig> = {
  bryllup: {
    type: 'bryllup',
    label: 'Bryllup',
    description: 'Planlegg den store dagen fra A til Å',
    icon: Heart,
    color: 'text-laks',
    bg: 'bg-laks-lys/30',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett', 'kjøreplan', 'bordplassering'],
    vendorCategories: [
      'Selskapslokale',
      'Catering',
      'Fotograf',
      'Blomster & dekor',
      'Kake',
      'Transport',
      'Musikk & underholdning',
    ],
    defaultTasks: [
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send save the date', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Book vigselssted', category: 'Lokale' },
      { title: 'Book festlokale', category: 'Lokale' },
      { title: 'Book fotograf', category: 'Leverandører' },
      { title: 'Book videograf', category: 'Leverandører' },
      { title: 'Book band eller DJ', category: 'Musikk' },
      { title: 'Velg og book catering / restaurant', category: 'Mat & drikke' },
      { title: 'Velg bryllupskake', category: 'Mat & drikke' },
      { title: 'Planlegg meny og drikkevarer', category: 'Mat & drikke' },
      { title: 'Velg brudekjole', category: 'Klær' },
      { title: 'Velg brudgommens antrekk', category: 'Klær' },
      { title: 'Velg brudebukett og blomsterdekor', category: 'Dekorasjon' },
      { title: 'Planlegg borddekorasjon', category: 'Dekorasjon' },
      { title: 'Lag bordplassering', category: 'Gjester' },
      { title: 'Bestill bryllupsreise', category: 'Reise' },
      { title: 'Skriv taler og tekster', category: 'Program' },
      { title: 'Lag program for dagen', category: 'Program' },
      { title: 'Send takk etter bryllupet', category: 'Etter' },
    ],
    defaultBudgetItems: [
      { name: 'Festlokale', category: 'Lokale', estimated: 30000 },
      { name: 'Catering / middag', category: 'Mat & drikke', estimated: 25000 },
      { name: 'Drikke', category: 'Mat & drikke', estimated: 8000 },
      { name: 'Bryllupskake', category: 'Mat & drikke', estimated: 4000 },
      { name: 'Fotograf', category: 'Leverandører', estimated: 18000 },
      { name: 'Band eller DJ', category: 'Musikk', estimated: 12000 },
      { name: 'Blomster og dekor', category: 'Dekorasjon', estimated: 8000 },
      { name: 'Brudekjole', category: 'Klær', estimated: 15000 },
      { name: 'Brudgommens antrekk', category: 'Klær', estimated: 5000 },
      { name: 'Bryllupsreise', category: 'Reise', estimated: 20000 },
      { name: 'Diverse / uforutsett', category: 'Annet', estimated: 5000 },
    ],
    defaultGuestGroups: ['Paret', 'Familie - Partner 1', 'Familie - Partner 2', 'Felles venner', 'Kollegaer'],
    scheduleDayLabel: 'bryllupsdagen',
  },
  forlovelse: {
    type: 'forlovelse',
    label: 'Forlovelse',
    description: 'Fest for å feire forlovelsen',
    icon: Gem,
    color: 'text-blaa',
    bg: 'bg-blaa-lys/30',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett'],
    vendorCategories: ['Selskapslokale', 'Catering', 'Fotograf', 'Blomster & dekor'],
    defaultTasks: [
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Book lokale', category: 'Lokale' },
      { title: 'Planlegg mat og drikke', category: 'Mat & drikke' },
      { title: 'Bestill kake', category: 'Mat & drikke' },
      { title: 'Planlegg underholdning', category: 'Program' },
    ],
    defaultBudgetItems: [
      { name: 'Festlokale', category: 'Lokale', estimated: 5000 },
      { name: 'Mat og drikke', category: 'Mat & drikke', estimated: 6000 },
      { name: 'Kake', category: 'Mat & drikke', estimated: 1500 },
      { name: 'Dekorasjon', category: 'Dekorasjon', estimated: 1000 },
      { name: 'Diverse', category: 'Annet', estimated: 1000 },
    ],
    defaultGuestGroups: ['Paret', 'Familie - Partner 1', 'Familie - Partner 2', 'Felles venner', 'Kollegaer'],
  },
  konfirmasjon: {
    type: 'konfirmasjon',
    label: 'Konfirmasjon',
    description: 'En dag å huske for livet',
    icon: GraduationCap,
    color: 'text-teal',
    bg: 'bg-teal-lys/20',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett', 'kjøreplan'],
    nameField: { label: 'Navn på konfirmant', placeholder: 'Sofie Dahl' },
    vendorCategories: ['Selskapslokale', 'Catering', 'Fotograf', 'Kake', 'Transport'],
    defaultTasks: [
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Book lokale', category: 'Lokale' },
      { title: 'Bestill catering eller lag mat selv', category: 'Mat & drikke' },
      { title: 'Bestill konfirmasjonskake', category: 'Mat & drikke' },
      { title: 'Book fotograf', category: 'Leverandører' },
      { title: 'Planlegg program for dagen', category: 'Program' },
      { title: 'Kjøp gave til konfirmanten', category: 'Annet' },
    ],
    defaultBudgetItems: [
      { name: 'Festlokale', category: 'Lokale', estimated: 5000 },
      { name: 'Mat og drikke', category: 'Mat & drikke', estimated: 8000 },
      { name: 'Konfirmasjonskake', category: 'Mat & drikke', estimated: 2000 },
      { name: 'Fotograf', category: 'Leverandører', estimated: 5000 },
      { name: 'Gave til konfirmant', category: 'Annet', estimated: 3000 },
      { name: 'Diverse', category: 'Annet', estimated: 1000 },
    ],
    defaultGuestGroups: ['Familie', 'Venner', 'Klasse / skole'],
    scheduleDayLabel: 'konfirmasjonsdagen',
  },
  dap: {
    type: 'dap',
    label: 'Dåp',
    description: 'Feir den nyeste i familien',
    icon: Baby,
    color: 'text-blaa',
    bg: 'bg-blaa-lys/20',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett'],
    nameField: { label: 'Navn på dåpsbarnet', placeholder: 'Lille Maja' },
    vendorCategories: ['Selskapslokale', 'Catering', 'Fotograf', 'Kake'],
    defaultTasks: [
      { title: 'Bestem dato (koordiner med kirken)', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Book lokale for middagen', category: 'Lokale' },
      { title: 'Planlegg mat og drikke', category: 'Mat & drikke' },
      { title: 'Bestill dåpskake', category: 'Mat & drikke' },
      { title: 'Velg dåpsantrekk', category: 'Klær' },
      { title: 'Kjøp dåpsgave', category: 'Annet' },
    ],
    defaultBudgetItems: [
      { name: 'Lokale / hjemme', category: 'Lokale', estimated: 2000 },
      { name: 'Mat og drikke', category: 'Mat & drikke', estimated: 5000 },
      { name: 'Dåpskake', category: 'Mat & drikke', estimated: 1500 },
      { name: 'Dåpsantrekk', category: 'Klær', estimated: 1000 },
      { name: 'Gave til dåpsbarnet', category: 'Annet', estimated: 1000 },
    ],
    defaultGuestGroups: ['Familie', 'Venner', 'Naboer'],
    scheduleDayLabel: 'dåpsdagen',
  },
  bursdag: {
    type: 'bursdag',
    label: 'Bursdag',
    description: 'Fest som fortjent',
    icon: Cake,
    color: 'text-terrakotta',
    bg: 'bg-laks-lys/20',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett'],
    nameField: { label: 'Navn på bursdagsbarnet', placeholder: 'Karen Holm' },
    vendorCategories: ['Selskapslokale', 'Catering', 'Fotograf', 'Kake'],
    defaultTasks: [
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Bestill bursdagskake', category: 'Mat & drikke' },
      { title: 'Planlegg mat og drikke', category: 'Mat & drikke' },
      { title: 'Planlegg underholdning', category: 'Program' },
      { title: 'Kjøp dekorasjoner', category: 'Dekorasjon' },
    ],
    defaultBudgetItems: [
      { name: 'Lokale', category: 'Lokale', estimated: 3000 },
      { name: 'Mat og drikke', category: 'Mat & drikke', estimated: 4000 },
      { name: 'Bursdagskake', category: 'Mat & drikke', estimated: 1500 },
      { name: 'Dekorasjon', category: 'Dekorasjon', estimated: 1000 },
      { name: 'Diverse', category: 'Annet', estimated: 500 },
    ],
    defaultGuestGroups: ['Familie', 'Venner', 'Kollegaer'],
  },
  jubileum: {
    type: 'jubileum',
    label: 'Jubileum',
    description: 'Merk en milepæl med stil',
    icon: Trophy,
    color: 'text-salvie',
    bg: 'bg-salvie/20',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett'],
    nameField: { label: 'Navn på jubilanten', placeholder: 'Per Olsen' },
    vendorCategories: ['Selskapslokale', 'Catering', 'Fotograf', 'Blomster & dekor', 'Kake', 'Musikk & underholdning'],
    defaultTasks: [
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Book lokale', category: 'Lokale' },
      { title: 'Book catering', category: 'Mat & drikke' },
      { title: 'Bestill jubileumskake', category: 'Mat & drikke' },
      { title: 'Book fotograf', category: 'Leverandører' },
      { title: 'Planlegg taler og program', category: 'Program' },
      { title: 'Lag presentasjon / bildeshow', category: 'Program' },
    ],
    defaultBudgetItems: [
      { name: 'Festlokale', category: 'Lokale', estimated: 8000 },
      { name: 'Catering', category: 'Mat & drikke', estimated: 10000 },
      { name: 'Jubileumskake', category: 'Mat & drikke', estimated: 2000 },
      { name: 'Fotograf', category: 'Leverandører', estimated: 5000 },
      { name: 'Underholdning', category: 'Musikk', estimated: 4000 },
      { name: 'Diverse', category: 'Annet', estimated: 1000 },
    ],
    defaultGuestGroups: ['Familie', 'Venner', 'Kollegaer'],
  },
  firmafest: {
    type: 'firmafest',
    label: 'Firmafest',
    description: 'Samle laget til en minneverdig kveld',
    icon: Building2,
    color: 'text-teal-mork',
    bg: 'bg-teal-lys/15',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett', 'kjøreplan'],
    nameField: { label: 'Navn på bedriften eller gjengen', placeholder: 'Bedrift AS' },
    vendorCategories: ['Selskapslokale', 'Catering', 'Fotograf', 'Musikk & underholdning'],
    defaultTasks: [
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Send invitasjon til ansatte', category: 'Gjester' },
      { title: 'Samle inn mattpreferanser og allergier', category: 'Gjester' },
      { title: 'Book lokale', category: 'Lokale' },
      { title: 'Book catering', category: 'Mat & drikke' },
      { title: 'Planlegg underholdning eller aktiviteter', category: 'Program' },
      { title: 'Book transport ved behov', category: 'Logistikk' },
      { title: 'Lag program for kvelden', category: 'Program' },
    ],
    defaultBudgetItems: [
      { name: 'Festlokale', category: 'Lokale', estimated: 15000 },
      { name: 'Catering', category: 'Mat & drikke', estimated: 20000 },
      { name: 'Drikke', category: 'Mat & drikke', estimated: 8000 },
      { name: 'Underholdning', category: 'Musikk', estimated: 8000 },
      { name: 'Transport', category: 'Reise', estimated: 3000 },
      { name: 'Diverse', category: 'Annet', estimated: 2000 },
    ],
    defaultGuestGroups: ['Avdeling 1', 'Avdeling 2', 'Ledelse'],
  },
  annet: {
    type: 'annet',
    label: 'Annet',
    description: 'Planlegg et arrangement som passer deg',
    icon: Sparkles,
    color: 'text-laks',
    bg: 'bg-krem',
    modules: ['oversikt', 'leverandorer', 'gjøremål', 'gjesteliste', 'budsjett'],
    nameField: { label: 'Navn på festen', placeholder: 'Sommerfest 2026' },
    vendorCategories: [
      'Selskapslokale',
      'Catering',
      'Fotograf',
      'Blomster & dekor',
      'Kake',
      'Transport',
      'Musikk & underholdning',
    ],
    defaultTasks: [
      { title: 'Bestem dato og sted', category: 'Planlegging' },
      { title: 'Sett opp budsjett', category: 'Planlegging' },
      { title: 'Lag gjesteliste', category: 'Gjester' },
      { title: 'Send invitasjoner', category: 'Gjester' },
      { title: 'Book lokale', category: 'Lokale' },
      { title: 'Planlegg mat og drikke', category: 'Mat & drikke' },
    ],
    defaultBudgetItems: [
      { name: 'Lokale', category: 'Lokale', estimated: 3000 },
      { name: 'Mat og drikke', category: 'Mat & drikke', estimated: 4000 },
      { name: 'Diverse', category: 'Annet', estimated: 1000 },
    ],
    defaultGuestGroups: ['Gruppe 1', 'Gruppe 2'],
  },
};

/** All event types in display order (for the onboarding grid). */
export const EVENT_TYPE_LIST: EventTypeConfig[] = Object.values(EVENT_TYPE_CONFIG);
